import { Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, of } from 'rxjs';
import config from '../../../../config.js';

export interface UserPermissionsSyncResponse {
  message?: string;
  success?: boolean;
}

export interface HasPermissionResponse {
  has_permission: boolean;
}

@Injectable({ providedIn: 'root' })
export class UserPermissionsService {
  private readonly base = config.API_GATEWAY_ENDPOINT?.replace(/\/$/, '') || '';
  private readonly endpoint = `${this.base}/user_permissions/sync`;

  // Global permission signals
  hasAnyBcrRole = signal<boolean>(false); // For global access control
  hasStatusDropdownPermission = signal<boolean>(false);
  hasUploadDocumentPermission = signal<boolean>(false); // For Replace File button

  // List of all valid BCR roles
  private readonly BCR_ROLES = [
    'BCR - BCS Team Member',
    'BCR - PST member',
    'BCR - Privacy Office team member',
    'BCR - IGA Coordinator',
    'BCR - DDQ Nominee',
    'XAZ-BCR Approver',
    'BCR - Viewer',
  ];

  constructor(private http: HttpClient) {}

  /**
   * Sync user permissions after login.
   * POST to /user_permissions/sync
   */

  syncUserPermissions(): Observable<UserPermissionsSyncResponse> {
    return this.http.post<UserPermissionsSyncResponse>(this.endpoint, {});
  }

  /**
   * Checks if user has permission for a specific role and module.
   * Updates the corresponding permission signal with the result.
   * This is called by AuthGuard after successful authentication.
   *
   * @param pridUser - User PRID (username before @)
   * @param roleName - Role name to check (e.g., 'BCR - PST member')
   * @param moduleName - Module name to check (e.g., 'Legal Entity List')
   * @param permissionSignal - Signal to update with the result
   */
  checkPermission(
    pridUser: string,
    roleName: string[],
    moduleName: string[],
    permissionSignal: ReturnType<typeof signal<boolean>>,
  ): void {
    if (!pridUser) {
      permissionSignal.set(false);
      return;
    }

    const endpoint = `${this.base}/user_permissions/has_permission`;
    const body = {
      prid_user: pridUser,
      role_name: roleName,
      module_name: moduleName,
    };

    this.http
      .post<HasPermissionResponse>(endpoint, body)
      .pipe(catchError(() => of({ has_permission: false })))
      .subscribe((res) => {
        permissionSignal.set(!!res?.has_permission);
      });
  }

  /**
   * Checks if user has ANY of the BCR roles.
   * This is used for global access control.
   * Returns an Observable that emits the response with has_permission property.
   *
   * @param pridUser - User PRID (username before @)
   * @returns Observable<HasPermissionResponse>
   */
  checkGlobalAccess(pridUser: string): Observable<HasPermissionResponse> {
    if (!pridUser) {
      this.hasAnyBcrRole.set(false);
      return of({ has_permission: false });
    }

    const endpoint = `${this.base}/user_permissions/has_permission`;
    const body = {
      prid_user: pridUser,
      role_name: this.BCR_ROLES,
      module_name: [], // Empty array means check roles regardless of module
    };

    return this.http.post<HasPermissionResponse>(endpoint, body).pipe(
      catchError(() => {
        this.hasAnyBcrRole.set(false);
        return of({ has_permission: false });
      }),
    );
  }
}
